import PrintIcon from "@mui/icons-material/Print";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { FieldValues, useFormContext } from "react-hook-form";
import { useDialog } from "../hooks/useDialog";
import { DialogProps } from "../misc/DialogProps";
import { Student } from "../providers/studentsProvider";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { Form, useGetList, useGetManyReference, useGetOne } from "react-admin";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { SelectInput } from "../inputs/SelectInput";
import { DrivingLicenseAuthority } from "../providers/drivingLicenseAuthoritiesProvider";
import { Column } from "../misc/Column";
import { serverAPI } from "../api/server.api";
import { LoadingButton } from "@mui/lab";
import { useState } from "react";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { DrivingSchool } from "../providers/drivingSchoolsProvider";
import { BooleanInput } from "../inputs/BooleanInput";
import { useBookedQuotes } from "../hooks/useBookedQuotes";
import { safeLocalStorage } from "../utils/safeLocalStorage";

export function FillOutFormButton({ student }: { student: Student }) {
  const { dialogProps, openDialog } = useDialog("fillOutFormDialog");
  return (
    <>
      <IconButton onClick={openDialog} data-testid="fillOutFormButton">
        <PrintIcon />
      </IconButton>
      <FillOutFormDialog {...dialogProps} student={student} />
    </>
  );
}

function FillOutFormDialog({ student, open, onClose }: DialogProps & { student: Student }) {
  const [{ drivingSchoolId }] = useAutovioContext();
  const { selectedBookedQuote } = useBookedQuotes(student);
  const { data: drivingSchool } = useGetOne<DrivingSchool>(
    "drivingSchools",
    { id: drivingSchoolId! },
    {
      enabled: !!drivingSchoolId,
    },
  );
  const { data: studentRelatedAuthorities = [], isLoading: isLoadingStudentRelatedAuthorities } =
    useGetManyReference<DrivingLicenseAuthority>(
      "drivingLicenseAuthorities",
      {
        target: "assignedBranchId",
        id: student.branchId,
        sort: { field: "name", order: "ASC" },
      },
      { enabled: !!student.branchId },
    );
  const { data: authorities = [], isLoading: isLoadingAuthorities } = useGetList<DrivingLicenseAuthority>(
    "drivingLicenseAuthorities",
    {
      filter: { assignedBranchesIds: drivingSchool?.branches.map((it) => it.id) },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: !!drivingSchool },
  );
  const [currentProcess, setCurrentProcess] = useState<"download" | "preview" | null>(null);
  const isLoading = isLoadingStudentRelatedAuthorities || isLoadingAuthorities;
  const defaultAuthority = studentRelatedAuthorities[0] ?? authorities[0];

  const submit =
    (action: "download" | "preview", { formId, authorityId, quoteId, withoutBackground }: FieldValues) =>
    async () => {
      if (!formId) return;
      const authority = authorities?.find((it) => it.id === authorityId);
      const form = authority?.forms.find((it) => it.id === formId);
      if (!authority || !form) return;
      setCurrentProcess(action);
      const [_, actualFormId] = formId.split(":");
      try {
        const pdf = await serverAPI.getFilledOutAuthorityFormPdf(student.id, actualFormId, quoteId, withoutBackground);
        const blob = new Blob([pdf], { type: "application/pdf" });
        if (action === "download") {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `Führerscheinstelle_${form.name}_${student.name}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          window.open(URL.createObjectURL(blob), "_blank");
        }
        setCurrentProcess(null);
      } catch (error) {
        setCurrentProcess(null);
        console.error("Error while fetching authority form PDF:", error);
        showErrorToast("Fehler beim Abrufen des Führerscheinstellen-Formulars");
      }
    };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Führerscheinstelle Formulare von {student.name}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <Form
          record={{}}
          defaultValues={{
            authorityId: defaultAuthority?.id ?? "",
            formId: "",
            quoteId: selectedBookedQuote.id,
            withoutBackground: false,
          }}
          onSubmit={submit("download", {})}
        >
          <DialogContent sx={{ py: 2 }}>
            <Column gap={3}>
              <SelectInput
                source="authorityId"
                options={authorities.map((it) => [it.id, it.name])}
                label="Führerscheinstellen"
              />
              <AuthorityDependentInputs authorities={authorities} student={student} />
            </Column>
          </DialogContent>
          <DialogActions sx={{ pt: 2 }}>
            <Actions submit={submit} currentProcess={currentProcess} />
          </DialogActions>
        </Form>
      )}
    </Dialog>
  );
}

function AuthorityDependentInputs({
  authorities,
  student,
}: {
  authorities: DrivingLicenseAuthority[];
  student: Student;
}) {
  const { watch } = useFormContext();
  const authorityId = watch("authorityId");
  const authorityForms = authorities.find((it) => it.id === authorityId)?.forms ?? [];
  const validAuthorityForms = authorityForms.filter((it) => !!it.pdfTemplate);
  const { bookedQuotes } = useBookedQuotes(student);
  const isPreviewStudentDetails2 = safeLocalStorage.getItem("previewStudentDetails2") === "on";

  if (validAuthorityForms.length === 0) {
    return <Typography>Keine Formulare für diese Führerscheinstelle verfügbar.</Typography>;
  }
  return (
    <>
      <SelectInput
        source="formId"
        options={validAuthorityForms.map((it) => [it.id, it.name])}
        label="Formulare"
        sx={{ width: "100%" }}
        testId="authoriyFormIdSelect"
      />
      {!isPreviewStudentDetails2 && bookedQuotes.length > 1 && (
        <SelectInput source="quoteId" options={bookedQuotes.map((it) => [it.id, it.bundleName])} label="Pakete" />
      )}
      <BooleanInput source="withoutBackground" label="Hintergrund entfernen" />
    </>
  );
}

function Actions({
  submit,
  currentProcess,
}: {
  submit: (action: "download" | "preview", data: FieldValues) => () => Promise<void>;
  currentProcess: "download" | "preview" | null;
}) {
  const { getValues, watch } = useFormContext();
  const formId = watch("formId");
  return (
    <>
      <LoadingButton
        onClick={submit("preview", getValues())}
        loading={currentProcess === "preview"}
        disabled={!formId || currentProcess === "download"}
        variant="outlined"
      >
        Vorschau
      </LoadingButton>
      <LoadingButton
        onClick={submit("download", getValues())}
        loading={currentProcess === "download"}
        disabled={!formId || currentProcess === "preview"}
        variant="contained"
        startIcon={<CloudDownloadIcon />}
      >
        Herunterladen
      </LoadingButton>
    </>
  );
}
